import { getGetApiV1ThreeLetterCodeClientAdminsClientKey } from '@fukuri-admins/apis/client/gen/endpoints';
import type { GetApiV1ThreeLetterCodeClientAdminsPrefectures200 } from '@fukuri-admins/apis/client/gen/model';
import { type UseFormReturn, useForm } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { mutate } from 'swr';
import { useGetClientAdminsClient } from '~/hooks';
import { useGetPrefectures } from '../../../../hooks/useGetPrefectures';
import { usePatchClientAdminsClient } from '../../../../hooks/usePatchClientAdminsClient';
import {
  convertToFormValues,
  type EditFormFieldValues,
  editFormSchema,
  generatePatchRequestBody,
} from '../../../../logics';
import type { ShippingAddressEditFormPresentation } from '../presentations';

type Args = {
  /** 企業コード */
  threeLetterCode: string;
  /** 保存成功時のコールバック（呼び出し側で確認ビューに戻す） */
  onSaved: () => void;
};

type IUseShippingAddressEditForm = (args: Args) => {
  state: {
    /** 編集フォームの状態（FormProvider に渡す） */
    methods: UseFormReturn<EditFormFieldValues>;
    /** 都道府県一覧 API レスポンス（converter で選択肢に変換する） */
    prefecturesData: GetApiV1ThreeLetterCodeClientAdminsPrefectures200;
    /** 保存 API 実行中かどうか */
    isSaving: boolean;
  };
  actions: {
    /** 保存ボタン（form submit）押下時の処理 */
    handleSubmit: React.ComponentProps<typeof ShippingAddressEditFormPresentation>['onSubmit'];
  };
};

/**
 * 送付先住所編集フォームの Component Hook
 *
 * - クライアント情報を取得し、編集フォームの初期値にする（確認ダイアログと同じキーなので再リクエストは発生しない）
 * - 都道府県一覧を取得する
 * - 保存 API（PATCH）の実行、成功・エラー時のトースト表示
 * - 成功時にクライアント情報を再取得（mutate）して確認ビューへ反映する
 */
export const useShippingAddressEditForm: IUseShippingAddressEditForm = ({
  threeLetterCode,
  onSaved,
}) => {
  const { showToast } = useToast();
  const { data } = useGetClientAdminsClient({ threeLetterCode });
  const { data: prefecturesData } = useGetPrefectures(threeLetterCode);

  const methods = useForm<EditFormFieldValues>({
    defaultValues: convertToFormValues(data),
    criteriaMode: 'all',
    mode: 'onBlur',
    resolver: zodResolver(editFormSchema),
  });

  const { trigger, isMutating: isSaving } = usePatchClientAdminsClient(threeLetterCode, {
    onSuccess: () => {
      showToast({ message: '発行内容を保存しました', status: 'success' });
      // クライアント情報を再取得して確認ビューに反映
      mutate(getGetApiV1ThreeLetterCodeClientAdminsClientKey(threeLetterCode));
      onSaved();
    },
    onError: error => {
      const errors = error.response?.data?.errors;
      if (errors && errors.length > 0) {
        showToast({ message: errors[0], status: 'danger' });
      } else {
        showToast({ message: '保存に失敗しました。', status: 'danger' });
      }
    },
  });

  const handleSubmit = methods.handleSubmit(async values => {
    await trigger(generatePatchRequestBody(values));
  });

  return {
    state: {
      methods,
      prefecturesData,
      isSaving,
    },
    actions: {
      handleSubmit,
    },
  };
};
